import UFunction from "./UFunction.mts";
import UObject from "./UObject.mts";

type Chainable<
  Factory extends Chainable.Factory,
  Modifiers,
> = ReturnType<Factory> & { [Key in keyof Factory]: Factory[Key] } & {
  [Key in keyof Modifiers]: Modifiers[Key] extends (
    options: any
  ) => (...args: infer Args) => any
    ? (...args: Args) => Chainable<Factory, Modifiers>
    : Chainable<Factory, Modifiers>;
};

namespace Chainable {
  export type Factory = (
    options: UObject.Any
  ) => UFunction.Any | Record<string, UFunction.Any>;

  // Typescript doesn't infer `options` from `T extends (options: UObject.Any)
  // => UFunction.Any` when function is passed directly in. For now we can wrap
  // it in `wrapFactory` to force the type to resolve before getting passed in.
  // TODO: Figure out how to force argument inference directly
  export const wrapFactory = <T, U extends T>(a: T): U => a as any;

  export type Modifier<Options extends UObject.Any> =
    | ((options: Options) => UFunction.Maybe<any[], Options>)
    | Options;

  export function create<
    Factory extends Chainable.Factory,
    const Modifiers extends Record<string, Modifier<Parameters<Factory>[0]>>,
  >(
    factory: Factory,
    modifiers: Modifiers,
    options: Parameters<Factory>[0] = {}
  ): Chainable<Factory, Modifiers> {
    return new Proxy(UFunction.noop, {
      get(_, p) {
        const modifier = (modifiers as any)[p];
        if (!modifier) {
          const finalizer = factory(options ?? {});

          if (p in finalizer)
            // @ts-expect-error
            return finalizer[p];

          return (factory as any)[p];
        }

        const next = (options: UObject.Any) =>
          create(factory, modifiers, options);

        if (typeof modifier === "function") {
          const res = modifier(options);
          if (typeof res === "function")
            return (...args: any[]) => next(res(...args));
          return next(res);
        }
        return next({ ...options, ...modifier });
      },
      apply(_factory, _, args) {
        const finalizer = factory(options ?? {});
        if (typeof finalizer !== "function")
          throw new Error("Finalizer is not a function");

        return finalizer(...args);
      },
    }) as any;
  }
}

export default Chainable;
